import { useTranslation } from 'react-i18next'
import { intlLocale } from '@/i18n'
import { cn } from '@/utils/cn'
import { formatDay } from '../format'
import type { Session } from '../types'

/** Sessions grouped by month; past ones dimmed, the next one highlighted. */
export function SessionList({ sessions, limit }: { sessions: Session[]; limit?: number }) {
  const { t } = useTranslation('courses')
  const today = new Date().toISOString().slice(0, 10)
  const nextIndex = sessions.findIndex((s) => s.date >= today)
  const shown = limit ? sessions.filter((s) => s.date >= today).slice(0, limit) : sessions
  if (shown.length === 0) return <p className="text-sm text-muted">{t('No sessions.')}</p>
  const months = new Map<string, Session[]>()
  for (const s of shown) {
    const key = s.date.slice(0, 7)
    months.set(key, [...(months.get(key) ?? []), s])
  }
  return (
    <div className="space-y-4">
      {[...months.entries()].map(([month, list]) => (
        <section key={month}>
          <h3 className="mb-2 text-xs font-semibold tracking-wide text-muted uppercase">
            {new Intl.DateTimeFormat(intlLocale(), { month: 'long', year: 'numeric' }).format(
              new Date(month + '-01T00:00:00'),
            )}
          </h3>
          <ul className="grid gap-1.5 sm:grid-cols-2">
            {list.map((s) => {
              const past = s.date < today
              const next = sessions[nextIndex] === s
              return (
                <li
                  key={`${s.date}-${s.startsAt}`}
                  className={cn(
                    'flex items-center justify-between rounded-control border px-3 py-2 text-sm',
                    next ? 'border-brand-300 bg-brand-50 font-medium text-brand-800' : 'border-line',
                    past && 'text-subtle',
                  )}
                >
                  <span>{formatDay(s.date)}</span>
                  <span className="tabular-nums">
                    {s.startsAt}–{s.endsAt}
                    {s.room ? ` · ${s.room}` : ''}
                  </span>
                </li>
              )
            })}
          </ul>
        </section>
      ))}
    </div>
  )
}
